import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Button,
  Select,
  TextInput,
  Alert,
  Center,
  Loader,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import CustomRichTextEditor from "../common/RichTextEditor";
import {
  Policy,
  PolicyCategory,
  DefaultPolicy,
  POLICY_CATEGORY_OPTIONS,
  POLICY_CATEGORY_LABELS,
  MAX_POLICY_TITLE_LENGTH,
} from "../../types/policies";

interface PolicyFormModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  editingPolicy: Policy | null;
  onSaved: () => void;
}

const EMPTY_BODY = "<p></p>";

interface PolicyFormValues {
  category: PolicyCategory;
  title: string;
  body: string;
}

const PolicyFormModal: React.FC<PolicyFormModalProps> = ({
  opened,
  onClose,
  salonId,
  editingPolicy,
  onSaved,
}) => {
  const { createPolicy, updatePolicy, getPolicyDefaults } = useApp();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<DefaultPolicy[]>([]);
  const [defaultsLoading, setDefaultsLoading] = useState(true);

  // Track whether the admin has manually edited the field, so changing the
  // category only auto-prefills an untouched title/body.
  const titleDirty = useRef(false);
  const bodyDirty = useRef(false);

  const form = useForm<PolicyFormValues>({
    initialValues: { category: "cancellation", title: "", body: "" },
    validate: {
      category: (value) => (!value ? "Category is required" : null),
      title: (value) => {
        if (!value || !value.trim()) return "Title is required";
        if (value.length > MAX_POLICY_TITLE_LENGTH)
          return `Title must be ${MAX_POLICY_TITLE_LENGTH} characters or less`;
        return null;
      },
      body: (value) =>
        !value || !value.trim() || value === EMPTY_BODY
          ? "Policy details are required"
          : null,
    },
  });

  // Mirror the latest form + defaults so the init effect can read them without
  // listing them as dependencies (`form` is re-created each render; we only want
  // to re-initialize when the modal opens or the defaults finish loading).
  const formRef = useRef(form);
  formRef.current = form;
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  // Load preset defaults once (title + body the picker prefills per category).
  useEffect(() => {
    let active = true;
    getPolicyDefaults()
      .then((data) => {
        if (active) setDefaults(data);
      })
      .catch(() => {
        // Defaults are a convenience; ignore failures and fall back to labels.
      })
      .finally(() => {
        if (active) setDefaultsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [getPolicyDefaults]);

  // Initialize the form when the modal opens. For a new policy we wait until the
  // preset defaults have loaded so the first open shows the proper default
  // title/body (and the inputs stay hidden until then).
  useEffect(() => {
    if (!opened) return;
    setSubmitError(null);
    const formApi = formRef.current;
    if (editingPolicy) {
      titleDirty.current = true;
      bodyDirty.current = true;
      formApi.setValues({
        category: editingPolicy.category,
        title: editingPolicy.title,
        body: editingPolicy.body,
      });
      formApi.resetDirty();
    } else if (!defaultsLoading) {
      titleDirty.current = false;
      bodyDirty.current = false;
      const preset = defaultsRef.current.find(
        (d) => d.category === "cancellation",
      );
      formApi.setValues({
        category: "cancellation",
        title: preset?.title ?? POLICY_CATEGORY_LABELS.cancellation,
        body: preset?.body ?? "",
      });
      formApi.resetDirty();
    }
  }, [opened, editingPolicy, defaultsLoading]);

  const handleCategoryChange = (value: string | null) => {
    if (!value) return;
    const category = value as PolicyCategory;
    const preset = defaults.find((d) => d.category === category);
    form.setFieldValue("category", category);

    if (!titleDirty.current) {
      form.setFieldValue(
        "title",
        preset?.title ?? POLICY_CATEGORY_LABELS[category],
      );
    }
    if (!bodyDirty.current) {
      form.setFieldValue("body", preset?.body ?? "");
    }
  };

  // Hide the inputs until defaults are ready (new policy only) so the user
  // can't start typing before the placeholder title/body are populated.
  const showForm = editingPolicy !== null || !defaultsLoading;

  const handleSubmit = async (values: PolicyFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (editingPolicy) {
        await updatePolicy({ id: editingPolicy.id, ...values });
      } else {
        await createPolicy({ salon_id: salonId, ...values });
      }
      onSaved();
      onClose();
    } catch (error: any) {
      setSubmitError(
        error?.message || "Failed to save policy. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editingPolicy ? "Edit Policy" : "Add New Policy"}
      size="lg"
    >
      {!showForm ? (
        <Center mih={220}>
          <Stack align="center" gap="xs">
            <Loader color="var(--brand-teal)" />
            <Text size="sm" c="dimmed">
              Loading policy defaults…
            </Text>
          </Stack>
        </Center>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {submitError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
              >
                {submitError}
              </Alert>
            )}

            <Select
              label="Category"
              data={POLICY_CATEGORY_OPTIONS}
              allowDeselect={false}
              {...form.getInputProps("category")}
              onChange={handleCategoryChange}
            />

            <TextInput
              label="Title"
              placeholder="e.g. Cancellation Policy"
              maxLength={MAX_POLICY_TITLE_LENGTH}
              description={`${form.values.title.length}/${MAX_POLICY_TITLE_LENGTH}`}
              {...form.getInputProps("title")}
              onChange={(event) => {
                titleDirty.current = true;
                form.setFieldValue("title", event.currentTarget.value);
              }}
            />

            <CustomRichTextEditor
              label="Policy Details"
              value={form.values.body}
              placeholder="Describe this policy for your clients..."
              minHeight={220}
              onChange={(html) => {
                bodyDirty.current = true;
                form.setFieldValue("body", html);
              }}
            />
            {form.errors.body && (
              <Alert color="red" p="xs">
                {form.errors.body}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="var(--brand-teal)"
                loading={isSubmitting}
              >
                {editingPolicy ? "Save Changes" : "Add Policy"}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
};

export default PolicyFormModal;
